import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Trash2, Loader2, FileCheck2, Download, XCircle, Paperclip } from 'lucide-react';
import { saveAs } from 'file-saver';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHANNELS = [['portal', 'Client portal'], ['email', 'Email'], ['courier', 'Courier / hand delivery'], ['other', 'Other']];

/** Container tab — amber warning when the deadline is ≤3 days away and nothing has been logged as submitted. */
export function SubmissionDueBanner({ project }) {
  if (!project.due_date || (project.submissions || []).length || ['won', 'lost'].includes(project.outcome)) return null;
  const days = Math.round((new Date(`${project.due_date}T00:00:00Z`) - new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z')) / 86400000);
  if (days < 0 || days > 3) return null;
  const versions = (project.packages || []).length;
  return (
    <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 text-amber-800 rounded-md px-3 py-2 text-xs" data-testid="rfp-submission-due-banner">
      <Send className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>
        <b>Deadline {days === 0 ? 'is today' : `in ${days} day${days === 1 ? '' : 's'}`} ({project.due_date})</b> — no submission logged yet.{' '}
        {versions ? `${versions} package version${versions === 1 ? '' : 's'} built; log the submission once it has gone to the client.` : 'Build the submission package on the Checklist tab, then log the submission here.'}
        {' '}The bid lead and leadership are reminded daily until it is logged.
      </span>
    </div>
  );
}

/** Dialog to record that a package version was submitted. */
export function LogSubmissionDialog({ project, pkg, open, onOpenChange, onLogged }) {
  const [form, setForm] = useState({ recipients: '', channel: 'portal', note: '', submitted_at: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  if (!pkg) return null;
  const save = async () => {
    setSaving(true);
    try {
      const recipients = form.recipients.split(/[,;\n]/).map(x => x.trim()).filter(Boolean);
      await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/packages/${pkg.id}/submit`, { ...form, recipients }, { withCredentials: true });
      toast.success(`Submission of v${pkg.version} logged`);
      onOpenChange(false); onLogged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not log submission'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rfp-submit-dialog">
        <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><Send className="w-4 h-4 text-[#4ABFCF]" />Log submission — package v{pkg.version}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-xs">
          <label className="block text-[10px] text-slate-500">Submitted to (names, emails or portal — comma separated)</label>
          <Input value={form.recipients} onChange={e => setForm(f => ({ ...f, recipients: e.target.value }))} placeholder="procurement@client.com, Ariba portal" className="h-8 text-xs" data-testid="rfp-submit-recipients" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="rfp-submit-channel">
              {CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input type="date" value={form.submitted_at} onChange={e => setForm(f => ({ ...f, submitted_at: e.target.value }))} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="rfp-submit-date" />
          </div>
          <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Note (confirmation number, who confirmed receipt…)" className="h-8 text-xs" data-testid="rfp-submit-note" />
          <Button size="sm" onClick={save} disabled={saving || !form.recipients.trim()} className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-submit-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}Log submission
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Attach the client's acknowledgement to a logged submission. */
function ConfirmationDialog({ project, sub, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ reference: '', confirmed_at: new Date().toISOString().slice(0, 10), note: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  if (!sub) return null;
  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      fd.append('reference', form.reference); fd.append('confirmed_at', form.confirmed_at); fd.append('note', form.note);
      await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/submissions/${sub.id}/confirmation`, fd, { withCredentials: true });
      toast.success(`Confirmation attached to v${sub.version}`); onOpenChange(false); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not attach confirmation'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rfp-confirm-dialog">
        <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-[#4ABFCF]" />Submission confirmation — v{sub.version}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-xs">
          <label className="block text-[10px] text-slate-500">Client acknowledgement (.eml .msg .pdf .png .jpg .docx .txt · ≤15 MB)</label>
          <input type="file" accept=".eml,.msg,.pdf,.png,.jpg,.jpeg,.docx,.txt" onChange={e => setFile(e.target.files?.[0] || null)} className="block w-full text-xs text-slate-600" data-testid="rfp-confirm-file" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Reference / receipt no." className="h-8 text-xs" data-testid="rfp-confirm-reference" />
            <input type="date" value={form.confirmed_at} onChange={e => setForm(f => ({ ...f, confirmed_at: e.target.value }))} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="rfp-confirm-date" />
          </div>
          <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Note (who acknowledged, portal status…)" className="h-8 text-xs" data-testid="rfp-confirm-note" />
          <Button size="sm" onClick={save} disabled={saving || (!file && !form.reference.trim() && !form.note.trim())} className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-confirm-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileCheck2 className="w-3.5 h-3.5 mr-1" />}Attach confirmation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Container tab — submission history. */
export function SubmissionLogCard({ project, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [confirmFor, setConfirmFor] = useState(null);
  const subs = [...(project.submissions || [])].sort((a, b) => (b.submitted_at || '').localeCompare(a.submitted_at || ''));
  const remove = async (s) => {
    if (!window.confirm(`Remove the submission record for v${s.version}?`)) return;
    try { await axios.delete(`${API_URL}/api/rfp/response/projects/${project.id}/submissions/${s.id}`, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove'); }
  };
  const download = async (s) => {
    try {
      const r = await axios.get(`${API_URL}/api/rfp/response/projects/${project.id}/submissions/${s.id}/confirmation/download`, { withCredentials: true, responseType: 'blob' });
      saveAs(r.data, s.confirmation.filename || 'confirmation');
    } catch { toast.error('Could not download the confirmation'); }
  };
  const removeConf = async (s) => {
    if (!window.confirm(`Remove the confirmation from v${s.version}?`)) return;
    try { await axios.delete(`${API_URL}/api/rfp/response/projects/${project.id}/submissions/${s.id}/confirmation`, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove'); }
  };
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4 space-y-2" data-testid="rfp-submission-log">
      <div className="flex items-center gap-2 flex-wrap">
        <Send className="w-4 h-4 text-[#4ABFCF]" />
        <span className="text-sm font-semibold text-[#0F2040]">Submission history</span>
        {subs.length > 0
          ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="rfp-submission-count">{subs.length} submission{subs.length === 1 ? '' : 's'} · last {String(subs[0].submitted_at).slice(0, 10)}</Badge>
          : <span className="text-[10px] text-slate-500">Nothing submitted yet — log a submission from a built package on the Checklist tab</span>}
      </div>
      {subs.length > 0 && (
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
          {subs.map(s => (
            <div key={s.id} className="p-2 flex items-center gap-2 text-xs flex-wrap" data-testid={`rfp-submission-${s.id}`}>
              <Badge variant="outline" className="text-[9px] bg-[#0F2040] text-white border-[#0F2040]">v{s.version}</Badge>
              <span className="font-mono text-[10px] text-[#0F2040]">{String(s.submitted_at).slice(0, 10)}</span>
              <span className="text-slate-600">to <b>{s.recipients.join(', ')}</b> via {s.channel}</span>
              {s.note && <span className="text-[10px] text-slate-400">— {s.note}</span>}
              {s.confirmation ? (
                <span className="inline-flex items-center gap-1" data-testid={`rfp-submission-confirmed-${s.id}`}>
                  <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" title={`${s.confirmation.note || ''} · attached by ${s.confirmation.by}`}>
                    <FileCheck2 className="w-2.5 h-2.5 mr-0.5" />Confirmed {s.confirmation.confirmed_at}{s.confirmation.reference ? ` · ref ${s.confirmation.reference}` : ''}
                  </Badge>
                  {s.confirmation.filename && <button type="button" onClick={() => download(s)} className="text-[10px] text-[#0F2040] hover:underline inline-flex items-center gap-0.5" data-testid={`rfp-submission-receipt-${s.id}`}><Download className="w-3 h-3" />receipt</button>}
                  {isAdmin && <button type="button" onClick={() => removeConf(s)} className="text-slate-300 hover:text-rose-500" title="Remove confirmation" data-testid={`rfp-submission-confirm-del-${s.id}`}><XCircle className="w-3 h-3" /></button>}
                </span>
              ) : (
                <button type="button" onClick={() => setConfirmFor(s)} className="text-[10px] text-indigo-700 hover:underline inline-flex items-center gap-0.5" data-testid={`rfp-submission-confirm-btn-${s.id}`}><Paperclip className="w-3 h-3" />Attach confirmation</button>
              )}
              <span className="ml-auto text-[10px] text-slate-400">{s.submitted_by}</span>
              {isAdmin && <button type="button" onClick={() => remove(s)} className="text-slate-300 hover:text-rose-500" title="Remove record" data-testid={`rfp-submission-del-${s.id}`}><Trash2 className="w-3 h-3" /></button>}
            </div>
          ))}
        </div>
      )}
      <ConfirmationDialog project={project} sub={confirmFor} open={!!confirmFor} onOpenChange={v => { if (!v) setConfirmFor(null); }} onSaved={onRefresh} />
    </div>
  );
}
